<template>
    <div>
        <p><span class="docInfoBodyNote">Validate Membership Information: </span>Kindly input the patient's complete HMI Membership and Personal Information. Everything must be filled out based on what is written on the member's HMI Card.</p>
        <v-form
            ref="form"
            v-model="valid"
            lazy-validation
            @submit.prevent="validate"
        >
            <v-text-field
                ref="membn"
                v-model.trim="membN"
                v-mask="'######'"
                :disabled="disable.validate"
                :rules="[v => !!v || 'Member Number is required']"
                label="Member Number"
                autocomplete="nope"
                required
                clearable
                color="#009688"
                :name="`member_${randomFieldName}`"
                @paste.prevent
                @copy.prevent
                @cut.prevent
                @contextmenu.prevent
            />
            <v-text-field
                v-model="birthDate"
                :disabled="disable.validate"
                :rules="dateRules"
                label="Birthdate"
                autocomplete="nope"
                required
                type="date"
                clearable
                color="#009688"
                :name="`birthDate_${randomFieldName}`"
                @paste.prevent
                @copy.prevent
                @cut.prevent
                @contextmenu.prevent
            />
            <v-btn
                style="margin-top: 3%; background-color: #009688; color: white;"
                class="docInfoBodyBtn"
                :loading="disable.validate"
                type="submit"
            >
                <v-icon>{{ mdiMagnify }}</v-icon>Validate Member
            </v-btn>
        </v-form>
        <v-dialog
            v-model="showNotEligible"
            width="500"
            persistent
            class="blackFont"
        >
            <v-card>
                <v-card-title class="text-h5">
                    <span v-if="isSessionExpired">Session Ended</span>
                    <span v-else>Validation Result</span>
                </v-card-title>
                <v-card-text v-if="isSessionExpired" style="height: 95px;">
                    <v-fade-transition hide-on-leave>
                        <p style="padding-top: 25px;" class="blackFont">
                            Your session has expired for security reasons. Please <span class="errHighlight">log in again</span> to continue.
                        </p>
                    </v-fade-transition>
                </v-card-text>
                <v-card-text v-else style="height: 95px;">
                    <v-fade-transition hide-on-leave>
                        <p v-show="membNotFound" class="notFound blackFont">
                            This member was <span class="errHighlight">NOT FOUND</span>. Please double-check the member number and birthdate.
                        </p>
                    </v-fade-transition>
                    <v-fade-transition hide-on-leave>
                        <div v-show="!loading&&!membNotFound" class="blackFont">
                            <p style="margin-bottom: 5px;">
                                A member has been found! Kindly confirm if the member details are correct:
                            </p>
                            <ul
                                v-if="membData"
                                class="blackFont"
                            >
                                <li>NAME: <span class="txtHighlight">{{ `${membData.LAST_M} ${membData.MIDI_M ? membData.MIDI_M : ''}` }}</span></li>
                                <li>SEX: <span class="txtHighlight">{{ membData.SEX }}</span></li>
                                <li>BIRTHDATE: <span class="txtHighlight">{{ moment(membData.BIRT_D).format('MMMM D, YYYY') }}</span></li>
                            </ul>
                        </div>
                    </v-fade-transition>
                    <v-fade-transition hide-on-leave>
                        <p v-show="loading&&!patientNotEligible" class="loadingNotEligible">
                            <v-progress-linear
                                indeterminate
                                color="#009688"
                            />
                        </p>
                    </v-fade-transition>
                    <v-fade-transition hide-on-leave>
                        <p v-if="patientNoAccess" class="notEligible">
                            This member has <span class="errHighlight">NO ACCESS</span> to this provider. If the member believes this is an error, kindly tell them to contact HMI for assistance.
                        </p>
                        <p v-else-if="patientNotEligible" class="notEligible">
                            This member is <span class="errHighlight">NOT ELIGIBLE</span> for diagnostic procedures. Please ask the member to contact HMI. Thank you.
                        </p>
                    </v-fade-transition>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        v-if="!isSessionExpired&&!loading&&!membNotFound"
                        class="txtHighlight"
                        color="error darken-4"
                        text
                        @click="closeModal"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        v-if="!isSessionExpired&&showNotEligible&&!patientNotEligible&&!membNotFound"
                        class="txtHighlight"
                        color="#009688"
                        text
                        @click="checkMembEligibility"
                    >
                        Proceed
                    </v-btn>
                    <v-btn
                        v-else-if="!isSessionExpired"
                        class="txtHighlight"
                        color="error darken-4"
                        text
                        @click="closeModal"
                    >
                        Close
                    </v-btn>
                    <v-btn
                        v-else
                        class="txtHighlight"
                        color="#009688"
                        text
                        @click="closeModal"
                    >
                        Okay
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mdiMagnify } from '@mdi/js'
import { moment } from '../../../utils/date-shim'
import { mask } from 'vue-the-mask'
import { mapActions, mapGetters } from 'vuex'

export default {
    directives: { mask },
    data () {
        return {
            mdiMagnify,
            randomFieldName: Math.random().toString(36).substr(2, 9),
            membNotFound: false,
            patientNoAccess: false,
            patientNotEligible: false,
            loading: false,
            showNotEligible: false,
            screen_width: document.documentElement.clientWidth + 9,
            membN: '',
            birthDate: '',
            valid: false,
            disable: {
                validate: false,
                proceed: false
            },
            membData: null,
            dateRules: [v => !!v || 'Birthdate is required',  v => !moment(v).utcOffset('+0800').startOf('day').isAfter(moment().utcOffset('+0800').startOf('day')) || 'Birthdate cannot be in the future']
        }
    },
    computed: {
        ...mapGetters(['membDetails', 'isPatientEligible', 'isSessionExpired']),
        cancelBtn() {
            return !this.loading && !this.membNotFound
        },
        proceedBtn() {
            return this.showNotEligible && !this.patientNotEligible && !this.patientNoAccess && !this.membNotFound
        }
    },
    watch: {
        membDetails (newVal) {
            if (newVal) {
                this.disable.validate       = true
                this.showNotEligible        = true
                if (newVal.status === 404) {
                    this.loading            = false
                    this.membNotFound       = true
                } else {
                    this.membData           = newVal.data
                }
            }
        },
        isPatientEligible (newVal) {
            if (newVal) {
                this.validate.proceed = false
                if (newVal.eligible) {
                    const arg = {
                        MEMB_N: this.membN,
                        TOKEN: newVal.token,
                        NAME: `${this.membDetails.data.LAST_M}`
                    }
                    this.closeModal()
                    this.$emit('patientEligible', arg)
                } else {
                    if (newVal.reason.length > 0)
                        this.patientNoAccess    = true
                    this.patientNotEligible     = true
                    this.showNotEligible        = true
                }
            }
        }
    },
    mounted() {
        window.addEventListener('resize', this.onResize)
        window.addEventListener('keydown', this.validateEvent)
        this.$nextTick(() => {
            if (this.$refs && this.$refs.membn) this.$refs.membn.focus()
        })
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize)
        window.removeEventListener('keydown', this.validateEvent)
    },
    methods: {
        ...mapActions(['getMembDetails', 'checkPatientEligibility', 'getCsrfToken']),
        closeModal() {
            this.showNotEligible        = false
            this.patientNotEligible     = false
            this.patientNoAccess        = false
            this.loading                = false
            this.membNotFound           = false
            this.membData               = ''
            this.disable.validate       = false
        },
        async checkMembEligibility() {
            this.disable.proceed    = true
            this.loading            = true
            const arg = {
                MEMB_N: this.membN,
                BIRTHDATE: this.birthDate,
                TYPE_T: 'O',
                MSU_C: this.$cookies.get('provider_id')
            }
            await this.checkPatientEligibility(arg)
        },
        customFilter(item, queryText, itemText) {
            const text          = itemText.toString().toLowerCase()
            const searchText    = queryText.toString().toLowerCase()
            return text === searchText
        },
        saveSuffix() {
            if (this.$refs.suffix) this.$refs.suffix.blur()
        },
        async validate () {
            if (this.$refs.form.validate()) {
                const arg = {
                    MEMB_N: this.membN,
                    BIRTHDATE: this.birthDate
                }
                await this.getMembDetails(arg)
            }
        },
        onResize() {
            this.screen_width = document.documentElement.clientWidth
            this.screen_width = this.screen_width + 9
        },
        validateEvent(event) {
            if (event.key === 'Enter') {
                if (this.proceedBtn) {
                    this.checkMembEligibility()
                } else if (this.cancelBtn || this.membNotFound || this.patientNotEligible || this.patientNoAccess) {
                    this.closeModal()
                }
            }
        }
    }
}
</script>

<style scoped>
.docInfoBodyNote {
    font-weight: 700;
}
.docInfoBodySubmit {
    padding: 2% 0;
}
.docInfoBodyBtn {
    margin-bottom: 7%;
}
    .loadingNotEligible {
        padding-top: 40px;
    }
    .notFound {
        padding-top: 17px;
    }
    .blackFont {
        color: black;
    }
    .errSearchDoctor {
        padding: 5px 5px 0 5px;
        margin: 0;
    }
    .errHighlight {
        font-weight: 800;
        color: darkred;
    }
    .txtHighlight {
        font-weight: 600;
    }
    .notEligible {
        color: black;
        padding-top: 20px;
    }
</style>
